/**
 * Storage adapter.
 *
 * En `local` (modo desarrollo sin backend) usa localStorage del navegador.
 * En `api` (producción) pega contra los endpoints REST.
 *
 * El modo se controla con VITE_STORAGE_MODE en .env
 *
 * Esto permite que Alejandro:
 * 1. Arranque el frontend hoy mismo sin esperar el backend
 * 2. Cambie a modo `api` flippeando una sola variable
 * 3. Mantenga la lógica de UI desacoplada de la fuente de datos
 */

import { http } from './http.js';

const MODE = import.meta.env.VITE_STORAGE_MODE || 'local';

// ============================================================================
// LOCAL ADAPTER (localStorage)
// ============================================================================

const localAdapter = {
  // QUOTES
  async listQuotes() {
    const raw = localStorage.getItem('xp_quotes') || '[]';
    return JSON.parse(raw);
  },
  async getQuote(id) {
    const list = await this.listQuotes();
    return list.find((q) => q.id === id) || null;
  },
  async saveQuote(quote) {
    const list = await this.listQuotes();
    const idx = list.findIndex((q) => q.id === quote.id);
    if (idx >= 0) list[idx] = quote;
    else list.push(quote);
    localStorage.setItem('xp_quotes', JSON.stringify(list));
    return quote;
  },
  async deleteQuote(id) {
    const list = await this.listQuotes();
    localStorage.setItem('xp_quotes', JSON.stringify(list.filter((q) => q.id !== id)));
  },

  // DISCOUNT CODES
  async listDiscountCodes() {
    const raw = localStorage.getItem('xp_discount_codes') || '[]';
    return JSON.parse(raw);
  },
  async saveDiscountCodes(codes) {
    localStorage.setItem('xp_discount_codes', JSON.stringify(codes));
    return codes;
  },

  // FOLIO COUNTER
  async nextFolio() {
    const current = parseInt(localStorage.getItem('xp_folio_counter') || '1000', 10);
    const next = current + 1;
    localStorage.setItem('xp_folio_counter', String(next));
    return `XP-${next}`;
  },

  // PUBLIC ACCESS (para vista del cliente)
  async getQuoteByPublicToken(token) {
    const list = await this.listQuotes();
    return list.find((q) => q.publicToken === token) || null;
  },
};

// ============================================================================
// API ADAPTER (backend REST)
// ============================================================================

const apiAdapter = {
  listQuotes: () => http.get('/quotes'),
  getQuote: (id) => http.get(`/quotes/${id}`),
  saveQuote: (quote) =>
    quote.id && !quote.isNew ? http.put(`/quotes/${quote.id}`, quote) : http.post('/quotes', quote),
  deleteQuote: (id) => http.delete(`/quotes/${id}`),

  listDiscountCodes: () => http.get('/discount-codes'),
  saveDiscountCodes: (codes) => http.put('/discount-codes', codes),

  nextFolio: () => http.post('/quotes/next-folio').then((r) => r.folio),

  getQuoteByPublicToken: (token) => http.get(`/public/quotes/${token}`),
};

// ============================================================================
// EXPORT
// ============================================================================

export const storage = MODE === 'api' ? apiAdapter : localAdapter;
export const STORAGE_MODE = MODE;
